import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Play, RefreshCw, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

/**
 * Portfolio ottimizzato:
 * - Griglia usa SOLO thumbnail (400px)
 * - Lightbox carica full size (1600px+)
 * - Lazy loading con IntersectionObserver
 * - Preload prime 4 above-the-fold
 * - Skeleton + fallback su errore
 */
export default function Portfolio() {
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxProject, setLightboxProject] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lightboxLoading, setLightboxLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, projectsRes] = await Promise.all([
          api.get('/categories'),
          api.get('/public/projects')
        ]);
        setCategories(categoriesRes.data);
        setProjects(projectsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(p => p.category_id === activeCategory);

  const openLightbox = (project, index = 0) => {
    setLightboxProject(project);
    setLightboxIndex(index);
    setLightboxOpen(true);
    setLightboxLoading(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxProject(null);
    document.body.style.overflow = '';
  };

  const nextImage = () => {
    if (lightboxProject?.media) {
      setLightboxLoading(true);
      setLightboxIndex((prev) => (prev + 1) % lightboxProject.media.length);
    }
  };

  const prevImage = () => {
    if (lightboxProject?.media) {
      setLightboxLoading(true);
      setLightboxIndex((prev) => (prev - 1 + lightboxProject.media.length) % lightboxProject.media.length);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxProject]);

  if (loading) {
    return (
      <div className="pt-32 pb-24 flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin text-muted-foreground" size={32} />
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="portfolio-page">
      {/* Header */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground">Portfolio</span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl mt-4 mb-6">I Miei Lavori</h1>
          <p className="font-accent text-xl text-muted-foreground max-w-2xl mx-auto">
            Una selezione dei progetti più significativi. Ogni immagine racconta una storia.
          </p>
        </motion.div>
      </div>

      {/* Category Filters */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3"
          data-testid="category-filters"
        >
          <button
            onClick={() => setActiveCategory('all')}
            data-testid="filter-all"
            className={`px-5 py-2 text-sm transition-colors ${
              activeCategory === 'all'
                ? 'bg-foreground text-background'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Tutti
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`filter-${cat.slug}`}
              className={`px-5 py-2 text-sm transition-colors ${
                activeCategory === cat.id
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </motion.div>
      </div>

      {/* Projects Grid - Uses THUMBNAILS for fast loading */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12">
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="projects-grid"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                data-testid={`project-card-${index}`}
              >
                <ProjectCard 
                  project={project} 
                  index={index}
                  onClick={() => openLightbox(project, 0)}
                  priority={index < 4} // Preload prime 4
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            Nessun progetto trovato in questa categoria.
          </div>
        )}
      </div>

      {/* Lightbox - Uses FULL SIZE images */}
      <AnimatePresence>
        {lightboxOpen && lightboxProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lightbox-backdrop flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="lightbox-modal"
          >
            {/* Close Button - Mobile optimized */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-[60] w-11 h-11 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm text-white/90 hover:text-white hover:bg-black/80 transition-all"
              style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
              data-testid="lightbox-close"
            >
              <X size={24} />
            </button>

            {/* Navigation - Mobile optimized with high z-index and visible buttons */}
            {lightboxProject.media?.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-[60] w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm text-white/90 hover:text-white hover:bg-black/80 active:scale-95 transition-all touch-manipulation"
                  style={{ marginLeft: 'env(safe-area-inset-left, 0px)' }}
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft size={28} className="sm:w-9 sm:h-9" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-[60] w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm text-white/90 hover:text-white hover:bg-black/80 active:scale-95 transition-all touch-manipulation"
                  style={{ marginRight: 'env(safe-area-inset-right, 0px)' }}
                  data-testid="lightbox-next"
                >
                  <ChevronRight size={28} className="sm:w-9 sm:h-9" />
                </button>
              </>
            )}

            {/* Image with Loading + Swipe Support */}
            <motion.div
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              drag={lightboxProject.media?.length > 1 ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(e, { offset, velocity }) => {
                const swipeThreshold = 50;
                const velocityThreshold = 500;
                if (offset.x < -swipeThreshold || velocity.x < -velocityThreshold) {
                  nextImage();
                } else if (offset.x > swipeThreshold || velocity.x > velocityThreshold) {
                  prevImage();
                }
              }}
              className="max-w-[90vw] max-h-[85vh] relative cursor-grab active:cursor-grabbing touch-pan-y"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image-container"
            >
              {lightboxProject.media?.[lightboxIndex]?.type === 'video' ? (
                lightboxProject.media[lightboxIndex].embed_url ? (
                  <iframe
                    src={lightboxProject.media[lightboxIndex].embed_url}
                    className="w-[80vw] h-[45vw] max-h-[80vh]"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={lightboxProject.media[lightboxIndex].url}
                    poster={lightboxProject.media[lightboxIndex].thumbnail_url || undefined}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-[90vw] max-w-[1400px] max-h-[85vh] aspect-video bg-black"
                    data-testid="lightbox-video"
                  />
                )
              ) : (
                <LightboxImage
                  src={lightboxProject.media?.[lightboxIndex]?.url || lightboxProject.cover_image}
                  thumbnailSrc={lightboxProject.media?.[lightboxIndex]?.thumbnail_url}
                  alt={lightboxProject.media?.[lightboxIndex]?.alt_text || lightboxProject.title}
                  onLoad={() => setLightboxLoading(false)}
                />
              )}
            </motion.div>

            {/* Swipe hint for mobile - shows only first time */}
            {lightboxProject.media?.length > 1 && (
              <div className="absolute bottom-20 left-1/2 -translate-x-1/2 sm:hidden text-white/50 text-xs flex items-center gap-2 pointer-events-none">
                <ChevronLeft size={14} />
                <span>Scorri per navigare</span>
                <ChevronRight size={14} />
              </div>
            )}

            {/* Info - adjusted for mobile safe area */}
            <div 
              className="absolute bottom-4 left-4 text-white z-[55]"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)' }}
            >
              <h3 className="font-heading text-xl sm:text-2xl">{lightboxProject.title}</h3>
              <p className="text-white/70 text-sm sm:text-base">{lightboxProject.location}</p>
              {lightboxProject.media?.length > 1 && (
                <p className="text-white/50 text-xs sm:text-sm mt-1">
                  {lightboxIndex + 1} / {lightboxProject.media.length}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * ProjectCard - Card singola con lazy loading
 * Usa THUMBNAIL per velocità massima
 */
function ProjectCard({ project, index, onClick, priority }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(priority);
  const ref = useRef(null);

  // Get thumbnail URL (cover_image thumb -> media[0] thumb -> fallbacks)
  const coverMedia = project.media?.find(m => m.url === project.cover_image);
  const thumbnailUrl = coverMedia?.thumbnail_url
    || (project.cover_image?.includes('_medium.') ? project.cover_image.replace('_medium.', '_thumb.').replace('/images/', '/thumbnails/') : null)
    || project.media?.[0]?.thumbnail_url 
    || project.cover_image 
    || project.media?.[0]?.url;

  // IntersectionObserver for lazy loading
  useEffect(() => {
    if (priority) return; // Skip for above-the-fold
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px', threshold: 0.01 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [priority]);

  // Preload for priority items
  useEffect(() => {
    if (priority && thumbnailUrl) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = thumbnailUrl;
      document.head.appendChild(link);
      return () => document.head.removeChild(link);
    }
  }, [priority, thumbnailUrl]);

  const handleRetry = (e) => {
    e.stopPropagation();
    setError(false);
    setLoaded(false);
  };

  return (
    <div
      ref={ref}
      className="group cursor-pointer"
      onClick={onClick}
    >
      <div className="aspect-[4/5] overflow-hidden bg-muted mb-4 relative">
        {/* Skeleton placeholder */}
        {!loaded && !error && (
          <div className="absolute inset-0 bg-muted animate-pulse" />
        )}

        {/* Error state with retry */}
        {error && (
          <div className="absolute inset-0 bg-muted flex flex-col items-center justify-center gap-2">
            <span className="text-muted-foreground text-sm">Immagine non disponibile</span>
            <Button variant="ghost" size="sm" onClick={handleRetry}>
              <RefreshCw size={14} className="mr-1" /> Riprova
            </Button>
          </div>
        )}

        {/* Image - only render when visible */}
        {isVisible && !error && (
          <img
            src={thumbnailUrl}
            alt={project.title}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => { setError(true); setLoaded(true); }}
            className={`w-full h-full object-cover img-zoom transition-opacity duration-500 ${
              loaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}

        {/* Video indicator */}
        {project.media?.some(m => m.type === 'video') && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
            <Play size={48} className="text-white" />
          </div>
        )}
      </div>
      <h3 className="font-heading text-xl mb-1 group-hover:text-muted-foreground transition-colors">
        {project.title}
      </h3>
      <p className="text-sm text-muted-foreground">{project.location}</p>
    </div>
  );
}

/**
 * LightboxImage - Immagine full-size con loading progressivo
 * Mostra thumbnail blur mentre carica full
 */
function LightboxImage({ src, thumbnailSrc, alt, onLoad }) {
  const [fullLoaded, setFullLoaded] = useState(false);
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [error, setError] = useState(false);

  const handleFullLoad = () => {
    setFullLoaded(true);
    onLoad?.();
  };

  const handleRetry = () => {
    setError(false);
    setFullLoaded(false);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 text-white">
        <span>Impossibile caricare l&apos;immagine</span>
        <Button variant="outline" onClick={handleRetry}>
          <RefreshCw size={14} className="mr-2" /> Riprova
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading spinner */}
      {!fullLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <Loader2 className="animate-spin text-white" size={32} />
        </div>
      )}

      {/* Thumbnail as blur placeholder */}
      {thumbnailSrc && !fullLoaded && (
        <img
          src={thumbnailSrc}
          alt={alt}
          onLoad={() => setThumbLoaded(true)}
          className={`max-w-full max-h-[85vh] object-contain transition-all duration-300 ${
            thumbLoaded ? 'blur-md opacity-70' : 'opacity-0'
          }`}
        />
      )}

      {/* Full resolution image */}
      <img
        src={src}
        alt={alt}
        onLoad={handleFullLoad}
        onError={() => setError(true)}
        className={`max-w-full max-h-[85vh] object-contain transition-opacity duration-500 ${
          fullLoaded ? 'opacity-100' : 'absolute inset-0 opacity-0'
        }`}
      />
    </div>
  );
}
